import { computed, ref, shallowRef } from 'vue';
import { States, type statesTypes } from './useStoreApiAction';

export interface ConcurrentRequest<T = any> {
  id: string;
  data?: T;
  error?: any;
  promise: Promise<T>;
}

type StoreApiAction<T extends (...args: any[]) => Promise<any>> = {
  request: T;
};

type ApiCallInput<T extends (...args: any[]) => Promise<any>> =
  | T
  | StoreApiAction<T>;

export function useConcurrentApi<T extends (...args: any[]) => Promise<any>>(
  apiCall: ApiCallInput<T>,
  maxConcurrency = 5,
) {
  // Extract the actual API call function
  const actualApiCall: T = (apiCall && typeof apiCall === 'object' && 'request' in apiCall) ?
    apiCall.request :
    apiCall as T;

  interface QueueItem {
    id: string;
    args: Parameters<T>;
    resolve: (value: Awaited<ReturnType<T>>) => void;
    reject: (error: any) => void;
  }

  const queue = ref<QueueItem[]>([]);
  const activeRequests = shallowRef<Map<string, ConcurrentRequest<Awaited<ReturnType<T>>>>>(new Map());
  const state = ref<statesTypes>();
  const completedCount = ref(0);
  const totalCount = ref(0);
  let isProcessing = false;

  const is = (otherState: statesTypes) => {
    return state.value === otherState;
  };

  const isNot = (otherState: statesTypes) => {
    return state.value !== otherState;
  };

  const hasActiveRequests = computed(() => activeRequests.value.size > 0);
  const hasQueue = computed(() => queue.value.length > 0);
  const isLoading = computed(() => state.value === States.LOADING);

  const processQueue = async () => {
    if(isProcessing) {
      return;
    }

    isProcessing = true;
    state.value = States.LOADING;

    while(queue.value.length > 0 || activeRequests.value.size > 0) {
      while(activeRequests.value.size < maxConcurrency && queue.value.length > 0) {
        const item = queue.value.shift();

        if(!item) break;

        const requestId = item.id;
        const requestPromise = actualApiCall(...item.args);

        const concurrentRequest: ConcurrentRequest<Awaited<ReturnType<T>>> = {
          id: requestId,
          promise: requestPromise,
        };

        activeRequests.value.set(requestId, concurrentRequest);

        requestPromise
          .then((result) => {
            const request = activeRequests.value.get(requestId);

            if(request) {
              (request as any).data = result;
            }

            item.resolve(result);
            completedCount.value++;
          })
          .catch((error) => {
            const request = activeRequests.value.get(requestId);

            if(request) {
              (request as any).error = error;
            }

            item.reject(error);
            completedCount.value++;
          })
          .finally(() => {
            activeRequests.value.delete(requestId);
          });
      }

      if(activeRequests.value.size > 0) {
        await Promise.race(
          Array.from(
            activeRequests.value.values(),
          ).map((r) =>
            r.promise.catch((error) => {
              console.error(error);
            })
          ),
        );
      }
    }

    if(activeRequests.value.size === 0 && queue.value.length === 0) {
      state.value = totalCount.value > 0 ? States.COMPLETE : undefined;
    }

    isProcessing = false;
  };

  const addRequest = (
    id: string,
    ...args: Parameters<T>
  ): Promise<Awaited<ReturnType<T>>> => {
    return new Promise((resolve, reject) => {
      queue.value.push({ id, args: args as any, resolve, reject });
      totalCount.value++;

      // Start processing queue
      processQueue();
    });
  };

  const clear = () => {
    queue.value = [];
    activeRequests.value.clear();
    completedCount.value = 0;
    totalCount.value = 0;
    state.value = undefined;
    isProcessing = false;
  };

  return {
    queue: computed(() => queue.value.map((item) => ({ id: item.id, args: item.args as any }))),
    activeRequests: computed(() => Array.from(activeRequests.value.values())),
    hasActiveRequests,
    hasQueue,
    isLoading,
    state,
    completedCount: computed(() => completedCount.value),
    totalCount: computed(() => totalCount.value),
    addRequest,
    clear,
    is,
    isNot,
  };
}
